import { createNoteUpdate, INotepadUpdate, IPreparedNote, SpecialNotes } from "@fokused-me/core";
import {
   ContentModeType,
   ContentPanelActionType,
   FMAppContentPanel,
   FMConfirmDialog,
   FMPanelNote,
   INodePanelParams,
   INoteTreeAction,
   LazyNoteTree,
   markdownRenderer,
   NoteTreeNodeButtonType,
} from "@fokused-me/web-ui";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { List, Map, OrderedMap, Set } from "immutable";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDesktop } from "../../../state/Desktop";
import { IBinPanelInfo } from "../../../state/Model";
import { IContentPanelProps } from "./ContentPanelSelector";
import { useBreadcrumbs } from "./Helpers";

export interface ITreeContentAreaProps extends IContentPanelProps {
   panelInfo: IBinPanelInfo;
}

export const BinContentPanel = ({ notepad, panelInfo, notepadState }: ITreeContentAreaProps) => {
   const theme = useTheme();
   const compact = useMediaQuery(theme.breakpoints.down("sm"));
   const { desktopInfo, toggleNoteSelection, toggleExpandPanel, closePanels } = useDesktop();

   const rootElRef = useRef<HTMLDivElement>();

   const { setPanelType } = useDesktop();

   const { ids, levels } = useMemo(
      () =>
         notepad
            ? notepad.query({
                 rootId: SpecialNotes.BIN,
                 expanded: Set(),
                 includeRoot: false,
              })
            : { ids: List<string>(), levels: Map<string, number>() },
      /* eslint-disable-next-line */
      [notepad, panelInfo.rootNoteId, notepadState]
   );

   const breadcrumbs = useBreadcrumbs(panelInfo, notepad);

   const handleModeChange = useCallback(
      (mode: ContentModeType) => setPanelType(panelInfo.id, mode),
      [panelInfo.id, setPanelType]
   );

   const handleNoteTreeAction = useCallback(
      (action: INoteTreeAction) => {
         switch (action.type) {
            case "select":
               toggleNoteSelection({ noteId: action.itemId, notepadId: panelInfo.notepadId });
               break;
         }
      },
      [panelInfo.notepadId, toggleNoteSelection]
   );

   const handlePrepareNodePanel = useCallback(
      (id: string, params: INodePanelParams) => {
         if (compact) {
            params.leftPosition = undefined;
            params.rightPosition = 70;
         } else {
            const x0 = (window.innerWidth - (rootElRef.current ? rootElRef.current.clientWidth : 0)) / 2;
            params.leftPosition = parseInt((params.leftPosition || 0).toString(), 10) + x0;
         }

         params.buttonsShow = Set(["select"]) as Set<NoteTreeNodeButtonType>;
         return params;
      },
      [compact]
   );

   const handleHasItemChanged = useCallback(
      (item: IPreparedNote, component: any, hasChanged: boolean) => {
         if (hasChanged) {
            return hasChanged;
         } else {
            return (levels.get(item.id) || 1) - 1 !== component.props.pads;
         }
      },
      [levels]
   );

   const selectedMap = useMemo(() => {
      let map = OrderedMap<string, string>();
      desktopInfo.selectedNotes.forEach((ref) => (map = map.set(ref.noteId, ref.noteId)));
      return map;
   }, [desktopInfo.selectedNotes]);

   const [openClearBinDialog, setOpenClearBinDialog] = useState(false);

   const handlePanelAction = useCallback(
      (action: ContentPanelActionType) => {
         switch (action) {
            case "expand":
               toggleExpandPanel(panelInfo.id);
               break;
            case "close":
               closePanels(List([panelInfo.id]));
               break;
            case "empty-bin":
               setOpenClearBinDialog(true);
               break;
         }
      },
      [panelInfo, closePanels, toggleExpandPanel, setOpenClearBinDialog]
   );

   const handleClearBinDialogResult = useCallback(
      (result: number) => {
         if (result === 0) {
            const update = createNoteUpdate({ type: "empty-bin" });
            notepad!.apply(List<INotepadUpdate>([update]));
         }
         setOpenClearBinDialog(false);
      },
      [notepad, setOpenClearBinDialog]
   );

   return (
      <FMAppContentPanel
         expanded={panelInfo.expanded}
         actions={Set<ContentPanelActionType>().add("empty-bin")}
         highlightedActions={Set()}
         disableActions={Set()}
         disableModes={Set<ContentModeType>().add("note").add("edit")}
         mode={panelInfo.type}
         breadcrumbs={breadcrumbs}
         onAction={handlePanelAction}
         onModeChange={handleModeChange}
         onBreadcrumbsNavigate={console.log}
         onSearchTextChange={console.log}
      >
         {ids.size ? (
            <LazyNoteTree
               notepadId={notepad?.getId() || ""}
               loadIncrement={10}
               onAction={handleNoteTreeAction}
               onNodeMove={console.log}
               onTextDropped={console.log}
               // onScrollPosition={setTreeViewScrollPos}
               expanded={Set()}
               textExpanded={Set()}
               // initialScrollPosition={
               //    (viewSettings && viewSettings.scrollPos) || 0
               // }
               // fontSize={navigatorFontSize}
               hasItemChanged={handleHasItemChanged}
               notes={ids.map((id) => notepad!.getNote(id)!)}
               levels={levels}
               rootRef={rootElRef}
               selected={selectedMap}
               prepareText={prepareText}
               prepareNodePanel={handlePrepareNodePanel}
            />
         ) : (
            <FMPanelNote text="" notepadId={panelInfo.notepadId} onNodeDropped={console.log} />
         )}
         <FMConfirmDialog
            open={openClearBinDialog}
            title="Empty Bin"
            text="Do you want to permanently delete all notes from Bin?"
            onResult={handleClearBinDialogResult}
         />
      </FMAppContentPanel>
   );
};

function prepareText(note: IPreparedNote) {
   const text = note.text;
   return text ? markdownRenderer.render(text) : "";
}
